/**
 * 对指定设备执行固件升级编排（APP 进 Boot / Boot 恢复同一入口）。
 * 可供 Pinia store（首页点击 update 设备）与 useFirmwareUpgrade 复用。
 */
import type { Router } from 'vue-router'
import { toast } from '@rapoo/element'
import type { BaseDevice } from '@rapoo/device-sdk'
import { isFirmwareAuthCancelled } from '@rapoo/device-sdk'
import { useFirmwareStore } from '@/stores/firmware'
import { defaultReportError } from '@/composables/useErrorLogger'
import { ensureFirmwareHostHooks } from '@/composables/registerFirmwareHostHooks'

const FW_DIAG = '[FW-DIAG]'

export async function runFirmwareUpdateForDevice(
  targetDevice: BaseDevice,
  options: { router: Router; failToast: string; upgradingToast: string },
): Promise<void> {
  const firmwareStore = useFirmwareStore()

  if (firmwareStore.isFirmwareUpdating) {
    toast.warning(options.upgradingToast)
    firmwareStore.openUpgradeProgressDialog()
    console.warn(FW_DIAG, 'upgrade already in progress, reopen dialog', {
      model: targetDevice.manifest.model,
      mcu: targetDevice.manifest.MCU,
      progress: firmwareStore.firmwareUpgradeProgress,
    })
    return
  }

  firmwareStore.isFirmwareUpdating = true
  firmwareStore.firmwareUpgradeProgress = 0
  firmwareStore.lockUpgradingDevice(targetDevice)
  firmwareStore.unregisterUpgradeProgress()
  firmwareStore.registerUpgradeProgress()
  firmwareStore.openUpgradeProgressDialog()

  const startedAt = Date.now()
  let lastProgress = 0
  let lastProgressAt = startedAt
  // 仅诊断：进度长时间不动时打 warn，不中断升级
  const stallTimer = window.setInterval(() => {
    const progress = firmwareStore.firmwareUpgradeProgress
    if (progress !== lastProgress) {
      lastProgress = progress
      lastProgressAt = Date.now()
      return
    }
    const stallMs = Date.now() - lastProgressAt
    if (stallMs >= 15_000) {
      console.warn(FW_DIAG, 'progress stalled (log only)', {
        model: targetDevice.manifest.model,
        mcu: targetDevice.manifest.MCU,
        driver: targetDevice.manifest.driver,
        connectionType: targetDevice.getConnectionType?.(),
        progress,
        stallMs,
        elapsedMs: Date.now() - startedAt,
        channelConnected: Boolean(targetDevice.channel?.isConnected),
      })
    }
  }, 5_000)

  try {
    console.info(FW_DIAG, 'upgrade start', {
      model: targetDevice.manifest.model,
      mcu: targetDevice.manifest.MCU,
      driver: targetDevice.manifest.driver,
      connectionType: targetDevice.getConnectionType?.(),
      productId: targetDevice.manifest.hardware.currentPid,
      isBoot: targetDevice.isFirmwareUpdateMode?.() ?? false,
    })

    ensureFirmwareHostHooks()

    const firmware = targetDevice.features.firmware
    if (!firmware) {
      throw new Error('firmware not found')
    }

    await firmware.updateFirmware()

    console.info(FW_DIAG, 'upgrade success', {
      model: targetDevice.manifest.model,
      elapsedMs: Date.now() - startedAt,
      progress: firmwareStore.firmwareUpgradeProgress,
    })

    firmwareStore.releaseUpgradingDeviceBeforeHome()
    firmwareStore.closeUpgradeProgressDialog()
    options.router.replace('/home')
  } catch (error) {
    if (isFirmwareAuthCancelled(error)) {
      console.warn(FW_DIAG, 'upgrade auth cancelled', {
        model: targetDevice.manifest.model,
        elapsedMs: Date.now() - startedAt,
      })
      firmwareStore.releaseUpgradingDeviceBeforeHome()
      firmwareStore.closeUpgradeProgressDialog()
      toast.warning(options.failToast)
      return
    }
    console.error(FW_DIAG, 'upgrade error', {
      model: targetDevice.manifest.model,
      mcu: targetDevice.manifest.MCU,
      elapsedMs: Date.now() - startedAt,
      progress: firmwareStore.firmwareUpgradeProgress,
      error,
    })
    console.error('firmware upgrade error', error)
    defaultReportError(`firmware upgrade error: ${JSON.stringify(error)}`)
    firmwareStore.releaseUpgradingDeviceBeforeHome()
    firmwareStore.closeUpgradeProgressDialog()
    toast.error(options.failToast)
    options.router.replace('/home')
  } finally {
    window.clearInterval(stallTimer)
    if (firmwareStore.upgradingDevice) {
      firmwareStore.releaseUpgradingDeviceBeforeHome()
    }
    firmwareStore.unregisterUpgradeProgress()
    firmwareStore.isFirmwareUpdating = false
  }
}
